import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Textarea } from '@/components/shadcn/textarea'

type ExistingRegisteredTool = RestSchemas['Messages.Agent.ExistingRegisteredTool']
type NewRegisteredTool = RestSchemas['Messages.Agent.NewRegisteredTool']

const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120, 'Name cannot exceed 120 characters'),
  functionName: z.string().max(120, 'Function name cannot exceed 120 characters').optional().default(''),
  moduleName: z.string().max(120, 'Module name cannot exceed 120 characters').optional().default(''),
  description: z.string().max(512, 'Description cannot exceed 512 characters').optional().default(''),
  defaultSystemPrompt: z.string().max(4000, 'Default system prompt cannot exceed 4000 characters').optional().default(''),
})

export type TToolForm = z.infer<typeof formSchema>

type ToolFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedTool?: ExistingRegisteredTool
}

export const ToolForm = (props: ToolFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const edit = !!props.selectedTool

  const { getToolRegistrationApiListTools, postToolRegistrationApiUpdateTool, putToolRegistrationApiAddTool } =
    useRestClient()

  const { mutate: updateTool, isPending: isUpdating } = postToolRegistrationApiUpdateTool.useMutation(undefined, {
    onSuccess: () => {
      getToolRegistrationApiListTools.invalidateQueries(undefined)
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Tool successfully updated')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error updating tool')
    },
  })

  const { mutate: addTool, isPending: isAdding } = putToolRegistrationApiAddTool.useMutation(undefined, {
    onSuccess: () => {
      getToolRegistrationApiListTools.invalidateQueries(undefined)
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Tool successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding tool')
    },
  })

  const submitHandler = useCallback(
    (data: TToolForm) => {
      if (edit && props.selectedTool) {
        const updatedTool: ExistingRegisteredTool = {
          id: props.selectedTool.id,
          name: data.name,
          functionName: data.functionName || null,
          moduleName: data.moduleName || null,
          description: data.description || null,
          defaultSystemPrompt: data.defaultSystemPrompt || null,
          createdAt: props.selectedTool.createdAt,
          updatedAt: props.selectedTool.updatedAt,
          whodunit: props.selectedTool.whodunit,
        }

        updateTool({
          body: updatedTool,
        })
      } else {
        const newTool: NewRegisteredTool = {
          name: data.name,
          functionName: data.functionName || null,
          moduleName: data.moduleName || null,
          description: data.description || null,
          defaultSystemPrompt: data.defaultSystemPrompt || null,
        }

        addTool({
          body: newTool,
        })
      }
    },
    [edit, props.selectedTool, addTool, updateTool]
  )

  const isLoading = isUpdating || isAdding
  if (isLoading) return <FormComponentLoading />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} submitHandler={submitHandler} />
  )
}

const FormComponent = (
  props: ToolFormProps & {
    submitHandler: (data: TToolForm) => void
  }
) => {
  const selectedTool: Partial<ExistingRegisteredTool> = props.selectedTool ?? {}
  const { name, functionName, moduleName, description, defaultSystemPrompt } = selectedTool
  const edit = !!props.selectedTool

  const defaultValues = useMemo(
    () => ({
      name: name ?? '',
      functionName: functionName ?? '',
      moduleName: moduleName ?? '',
      description: description ?? '',
      defaultSystemPrompt: defaultSystemPrompt ?? '',
    }),
    [name, functionName, moduleName, description, defaultSystemPrompt]
  )

  const form = useForm<TToolForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const handleInvalidSubmit = useCallback(() => {
    toast.error('Please fix validation errors before submitting')
  }, [])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(props.submitHandler, handleInvalidSubmit)}>
        <div className="flex h-full flex-col">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="functionName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Function Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="moduleName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Module Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="defaultSystemPrompt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default System Prompt</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={6} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="form-action-container">
            <Button type="submit">{edit ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </form>
    </Form>
  )
}
